import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { openAiModels } from '../../../variables/OpenAi';

const openAiKeys = [
  'open_ai_api_key',
  'open_ai_model',
  'order_whatsapp_message_prompt',
  'offer_description_prompt',
];

export const OpenAiSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.OpenAI)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.OpenAI, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings.filter((s) => openAiKeys.includes(s.key)))
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxSizing: 'border-box',
      }}
    >
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            ...sharedStyles.h5,
            fontWeight: 'bold',
          }}
        >
          OpenAI
        </Typography>

        <Button
          size='small'
          endIcon={<OpenInNewIcon />}
          sx={{ ...sharedStyles.buttonText }}
          onClick={() =>
            window.open(
              'https://openai.com/api/pricing/',
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          Fiyatlama
        </Button>
      </Box>

      <Divider />

      <SettingsRow
        name='API Key'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'open_ai_api_key')}
        onChange={(newValue) => handleChange('open_ai_api_key', newValue)}
        masked
      />
      <SettingsRow
        name='Model'
        valueType={SettingsValueType.SELECT}
        value={getSettingsValue(settings, 'open_ai_model')}
        onChange={(newValue) => handleChange('open_ai_model', newValue)}
        options={openAiModels.map((model) => ({ value: model, label: model }))}
      />
      <SettingsRow
        name='Sipariş Mesajı Komutu'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'order_whatsapp_message_prompt')}
        onChange={(newValue) =>
          handleChange('order_whatsapp_message_prompt', newValue)
        }
        multiline
      />
      <SettingsRow
        name='Kampanya Açıklaması Komutu'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'offer_description_prompt')}
        onChange={(newValue) =>
          handleChange('offer_description_prompt', newValue)
        }
        multiline
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />

      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
